import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    House,
    Globe,
    Users,
    Plugs,
    ArrowsClockwise,
    SignOut,
    MagnifyingGlass,
    ShieldCheck,
} from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/domains", label: "Domains", icon: Globe },
    { to: "/migrations", label: "Migrations", icon: ArrowsClockwise },
    { to: "/registrar-accounts", label: "Registrar Accounts", icon: Plugs, admin: true },
    { to: "/users", label: "Users", icon: Users, admin: true },
];

function GlobalSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);
    const boxRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                inputRef.current?.focus();
            }
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!q.trim()) {
            setResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/domains/search", { params: { q, limit: 8 } });
                setResults(data);
                setOpen(true);
            } catch {
                setResults([]);
            }
        }, 180);
        return () => clearTimeout(timer);
    }, [q]);

    return (
        <div ref={boxRef} className="relative w-full max-w-lg">
            <div className="glass-input flex items-center gap-2 !py-2.5 !pl-4">
                <MagnifyingGlass size={18} weight="duotone" className="text-[color:var(--dm-text-2)]" />
                <input
                    ref={inputRef}
                    data-testid="global-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => q && setOpen(true)}
                    placeholder="Search any domain across all accounts…"
                    className="w-full bg-transparent outline-none text-sm"
                />
                <span className="kbd hidden md:inline">⌘K</span>
            </div>
            {open && results.length > 0 && (
                <div className="glass-strong absolute left-0 right-0 top-[110%] z-50 py-2 max-h-96 overflow-auto">
                    {results.map((r) => (
                        <button
                            key={r.id}
                            data-testid={`global-search-result-${r.domain_name}`}
                            onClick={() => {
                                setOpen(false);
                                setQ("");
                                navigate(`/domains?q=${encodeURIComponent(r.domain_name)}`);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-[rgba(0,122,255,0.06)] flex items-center justify-between gap-3"
                        >
                            <div className="flex flex-col">
                                <span className="mono text-sm">{r.domain_name}</span>
                                <span className="text-xs text-[color:var(--dm-text-2)]">
                                    {r.registrar_account_name}
                                </span>
                            </div>
                            <span
                                className={`pill ${
                                    r.registrar === "godaddy" ? "pill-warn" : "pill-brand"
                                }`}
                            >
                                {r.registrar}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const doLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex">
            <aside className="glass m-4 mr-2 w-64 shrink-0 p-4 flex flex-col sticky top-4 h-[calc(100vh-2rem)]">
                <Link to="/dashboard" className="block px-2 py-2">
                    <Logo />
                </Link>
                <nav className="mt-6 flex-1 space-y-1">
                    {NAV.filter((n) => !n.admin || user?.role === "admin").map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`sidebar-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    "sidebar-item " + (isActive ? "active" : "")
                                }
                            >
                                <Icon size={20} weight="duotone" />
                                <span className="text-sm">{n.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="glass !rounded-xl p-3 text-xs">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={16} weight="duotone" className="text-[color:var(--dm-success)]" />
                        <span className="font-semibold">{user?.name}</span>
                    </div>
                    <div className="text-[color:var(--dm-text-2)] mono truncate mb-2">
                        {user?.email}
                    </div>
                    <div className="flex items-center justify-between">
                        <span
                            className={`pill ${user?.role === "admin" ? "pill-brand" : "pill-neutral"}`}
                        >
                            {user?.role}
                        </span>
                        <button
                            data-testid="logout-button"
                            onClick={doLogout}
                            className="text-[color:var(--dm-text-2)] hover:text-[color:var(--dm-danger)] transition"
                            title="Sign out"
                        >
                            <SignOut size={18} weight="duotone" />
                        </button>
                    </div>
                </div>
            </aside>

            <main className="flex-1 min-w-0 p-4 pl-2">
                <div className="glass sticky top-4 z-40 flex items-center gap-4 px-4 py-3 mb-4">
                    <GlobalSearch />
                </div>
                <div className="pb-8 min-w-0">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
